import type {
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react'
import type { BadgeColor } from '../Badge'

export type InputFieldVariant =
  | 'phone'
  | 'email'
  | 'amount'
  | 'card-number'
  | 'tags'
  | 'password'
  | 'date'
  | 'number'
  | 'verification'
  | 'verification-v2'
  | 'time'
  | 'address'
  | 'message'

export type InputFieldPreviewState =
  | 'default'
  | 'focused'
  | 'filled'
  | 'disabled'
  | 'error'
  | 'info'
  | 'optional'
  | 'tooltip'

export type InputFieldTag = {
  label: string
  color: BadgeColor
}

export const INPUT_FIELD_VARIANTS: InputFieldVariant[] = [
  'phone',
  'email',
  'amount',
  'card-number',
  'tags',
  'password',
  'date',
  'number',
  'verification',
  'verification-v2',
  'time',
  'address',
  'message',
]

export const INPUT_FIELD_PREVIEW_STATES: InputFieldPreviewState[] = [
  'default',
  'focused',
  'filled',
  'disabled',
  'error',
  'optional',
  'tooltip',
  'info',
]

export const INPUT_FIELD_VARIANT_LABELS: Record<InputFieldVariant, string> = {
  phone: 'Phone number',
  email: 'Email',
  amount: 'Amount',
  'card-number': 'Card number',
  tags: 'Tags',
  password: 'Password',
  date: 'Date',
  number: 'Number',
  verification: 'Verification code',
  'verification-v2': 'Verification v2',
  time: 'Time',
  address: 'Street address',
  message: 'Message',
}

export const INPUT_FIELD_PREVIEW_STATE_LABELS: Record<
  InputFieldPreviewState,
  string
> = {
  default: 'Default',
  focused: 'Focused',
  filled: 'Filled',
  disabled: 'Disabled',
  error: 'Error',
  optional: 'Optional',
  tooltip: 'Tooltip',
  info: 'Info',
}

export const INPUT_FIELD_PASSWORD_DEMO_VISIBLE = 'Pass_88#!'

export const INPUT_FIELD_PASSWORD_DEMO_DOTS_COUNT = 9

export const INPUT_FIELD_ADDRESS_DEMO_SEGMENTS = [
  '123',
  'Hyde park ave.',
  'Los Angeles',
  'CA',
] as const

export const INPUT_FIELD_DEMO_VALUES: Record<InputFieldVariant, string> = {
  phone: '(201) 555-0179',
  email: 'address@email.com',
  amount: '1,000.00',
  'card-number': '4111 1111 1111 1111',
  tags: '',
  password: '••••••••',
  date: '04 / 21 / 2026',
  number: '400',
  verification: '045789',
  'verification-v2': '',
  time: '04 : 21 PM',
  address: '123 Hyde park ave. Los Angeles CA',
  message: 'Hello, this is a sample message.',
}

export const INPUT_FIELD_PLACEHOLDERS: Record<InputFieldVariant, string> = {
  phone: '(201) 555-0179',
  email: 'address@email.com',
  amount: '1,000.00',
  'card-number': '4111 1111 1111 1111',
  tags: 'Add a tag',
  password: 'Enter password',
  date: 'Select date',
  number: 'Enter number',
  verification: 'Enter number',
  'verification-v2': '',
  time: 'Select time',
  address: 'Address line 1',
  message: 'Write a message',
}

export const INPUT_FIELD_DEMO_TAGS: InputFieldTag[] = [
  { label: 'Tag #1', color: 'gray' },
  { label: 'Tag #2', color: 'yellow' },
  { label: 'Tag #3', color: 'cyan' },
]

export const INPUT_FIELD_TAG_COLORS: InputFieldTag['color'][] = [
  'gray',
  'yellow',
  'cyan',
  'blue',
  'vivid-green',
  'purple',
]

export type InputFieldProps = {
  label?: string
  variant?: InputFieldVariant
  /** Static visual state for the design-system showcase only. */
  previewState?: InputFieldPreviewState
  helperText?: string
  optional?: boolean
  showTooltip?: boolean
  tooltipLabel?: string
  clearable?: boolean
  onClear?: () => void
  actionLabel?: string
  onAction?: () => void
  tags?: InputFieldTag[]
  onTagsChange?: (tags: InputFieldTag[]) => void
  numberStep?: number
  numberMin?: number
  numberMax?: number
  otpLength?: number
  maxLength?: number
  showCounter?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  /** Dark surface styling. */
  darkMode?: boolean
  /** Fill the parent width (ignores the variant’s preferred max-width cap). */
  fullWidth?: boolean
  className?: string
  id?: string
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  onDateChange?: (date: Date) => void
  onTimeChange?: (date: Date) => void
  isDateDisabled?: (date: Date) => boolean
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'prefix' | 'size' | 'id' | 'onChange'
> &
  Pick<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'>
